import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { Autocomplete, Box, Checkbox, FormControlLabel, TextField, IconButton, Tooltip } from "@mui/material";
import { red, green } from '@mui/material/colors';
import {FilterAlt, ViewComfySharp, Transform, EditNote} from "@mui/icons-material";

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { SettingsButton, FilterButton } from "@plannel/components/ActionIconButton";
import {cloneDeep, includes, isArray, isEmpty, isEqual, isNull, isUndefined, keys, size, sortBy, values, find} from "lodash";

import rpReviewService from "@plannel/services/rp/rp-review-service";

import SplitterContainer from "@plannel/components/SplitterContainer";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import PaginationContainer from "@plannel/components/PaginationContainer"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import Snackbar from "@plannel/components/Snackbar";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import AbcXyzChip, { AbcXyzChipInline } from "@plannel/components/aggrid/AbcXyzChip";
import axiosUtil from "@plannel/utils/axios-bigint";
import { RPFilterModal } from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/FilterModal";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import InvCovDaysSettings from "@plannel/components/modal/InvCovDaysSettings";

import "@plannel/scss/components/CustomGrid.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import LayoutListModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/LayoutListModal";
import p13nService from "@plannel/services/system/p13n-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import NotesModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/NotesModal";
import AbcXyzFilterModal from "@plannel/components/modal/AbcXyzFilterModal";
import ZeroExclusionFilter from "@plannel/components/filter/ZeroExclusionFilter";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import DimensAttrModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/DimensAttrModal";
import companyService from "@plannel/services/data/company-service";
import FilterStatusPanel, { getFilterStore, extractFilterInfo } from  "@plannel/components/aggrid/FilterStatusPanel";
import RpUtil from "../utils/rp-util";
import { FS } from "@plannel/utils/font-util";

const { BOH, BOH_AMT, DEMAND_QTY, SIM_DEMAND_QTY, DEMAND_AMT, KIT_FCST_DEMAND_QTY, KIT_FCST_DEMAND_AMT, FCST_DEMAND_QTY, FCST_DEMAND_AMT, INTERNAL_DEMAND_QTY, INTERNAL_DEMAND_AMT, DOWNSTREAM_DEMAND_QTY_SUM, DOWNSTREAM_DEMAND_AMT_SUM, DOWNSTREAM_EOH_QTY_SUM, DOWNSTREAM_EOH_AMT_SUM, REPLENISH_PLAN_QTY, REPLENISH_PLAN_AMT, PURCHASE_PLAN_QTY, PURCHASE_PLAN_AMT, ARRIVAL_QTY, ARRIVAL_QTY_PLAN, ARRIVAL_QTY_CFM, ARRIVAL_AMT, SHIPMENT_QTY, SHIPMENT_QTY_PLAN, SHIPMENT_QTY_CFM, SHIPMENT_AMT, SHIPMENT_QTY_FCST, SHIPMENT_AMT_FCST, SHIPMENT_QTY_REPL, SHIPMENT_AMT_REPL, TARGET_STOCK, TARGET_STOCK_AMT, ROP, EXP_INV_QTY, EXP_INV_AMT, EOH, EOH_AMT, BACKORDER_QTY, INV_COV_DAYS, SHIPMENT_SHORTAGE_QTY, SIM_BOH, SIM_EOH, SIM_BACKORDER_QTY, SIM_ARRIVAL, SIM_SHIPMENT_QTY, SIM_INV_COV_DAYS, PROD_QTY } = CodeUtil.rpMeasure;
const INV_CLASS_CD = "invClassCd";
const { snackBarMessageColor } = ColorUtil;
const { itemType, versionStatusCode, muicolor, item, filterType, inventoryCoverageDaysPolicy, buckets: { MONTH }, dpABCConfig } = CodeUtil;
const { INV_COV_DAYS_THRESHOLD, INV_COV_DAYS_APPLY, INV_COV_DAYS_COMPUTED_METHOD } = inventoryCoverageDaysPolicy;
const { SIMULATION, PLANNED, RELEASED } = versionStatusCode;
const { CHECKBOX_SELECTED } = GridUtils.rowSelectedSource;
const RP_REVIEW = "RP_REVIEW";
const EXCEL_EXPORT = "excelExport";
const bucketDaysObj = {
  day: 1,
  week: 7,
  month: 30,
};
const gridId = "grid";

// 노트 마커를 그릴 컬럼 우선순위 — 앞 컬럼이 숨겨지면 다음 컬럼이 대신 표시
const NOTE_MARKER_COLUMNS = [["itemNmGrp", "itemCdGrp"], ["destLocNmGrp"]];
const NOTE_MARKER_GROUP_FIELD = { itemNmGrp: "itemNm", itemCdGrp: "itemCd", destLocNmGrp: "destLocNm" };
const NOTE_MARKER_REFRESH_COLS = ["itemNmGrp", "itemCdGrp", "destLocNmGrp"];
const NOTE_SIM_MEASURE = {
  [DEMAND_QTY]: SIM_DEMAND_QTY,
  [BOH]: SIM_BOH,
  [EOH]: SIM_EOH,
  [BACKORDER_QTY]: SIM_BACKORDER_QTY,
  [ARRIVAL_QTY]: SIM_ARRIVAL,
  [SHIPMENT_QTY]: SIM_SHIPMENT_QTY,
  [INV_COV_DAYS]: SIM_INV_COV_DAYS,
};


const salesKitMeasures = [BOH, BOH_AMT, DEMAND_QTY, DEMAND_AMT, FCST_DEMAND_QTY, FCST_DEMAND_AMT, INTERNAL_DEMAND_QTY, INTERNAL_DEMAND_AMT, REPLENISH_PLAN_QTY, REPLENISH_PLAN_AMT, ARRIVAL_QTY, ARRIVAL_AMT, SHIPMENT_QTY, SHIPMENT_QTY_CFM, SHIPMENT_QTY_PLAN, SHIPMENT_AMT, SHIPMENT_QTY_FCST, SHIPMENT_AMT_FCST, SHIPMENT_QTY_REPL, SHIPMENT_AMT_REPL,EXP_INV_QTY, EXP_INV_AMT, TARGET_STOCK, TARGET_STOCK_AMT, EOH, EOH_AMT, BACKORDER_QTY, INV_COV_DAYS, SHIPMENT_SHORTAGE_QTY, PROD_QTY];

const RpReview = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const userInfo = axiosUtil.jsonbigParse(localStorage.getItem("user"));
  const currency = userInfo?.currencySymbol;

  const pageSizes = [10, 30, 50];
  const resultTotalCount = useRef(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ? reduxViewState.rpVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { originLocation: null, destLocation: null, category: null });
  const itemTypes = ["FG", "KIT"];
  const [itemTypeSearch, setItemTypeSearch] = useState(reduxViewState?.itemTypeSearch ?? "FG");
  const [onlyExceptionApplied, setOnlyExceptionApplied] = useState(reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "onlyExceptionApplied") ? reduxViewState.onlyExceptionApplied : false);
  const [itemFilterColor, setItemFilterColor] = useState(size(reduxViewState?.itemFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
  const [itemFilterList, setItemFilterList] = useState(reduxViewState?.itemFilterList ?? {});
  const [itemFilterType, setItemFilterType] = useState(reduxViewState?.itemFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [invClassCdList, setInvClassCdList] = useState(reduxViewState?.invClassCdList ?? []);
  const [appliedExceptions, setAppliedExceptions] = useState(reduxViewState?.appliedExceptions ?? []);
  const [itemGrpLevel, setItemGrpLevel] = useState(reduxViewState?.grpLevelList ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(
    (reduxViewState?.advancedFilters?.advancedFilter?.children?.length > 0 || reduxViewState?.advancedFilters?.itemLevelCd)
      ? reduxViewState.advancedFilters : null
  );
  const [pendingAdvancedFilters, setPendingAdvancedFilters] = useState(null);
  const [isAFActive, setIsAFActive] = useState(
    !!(reduxViewState?.advancedFilters?.advancedFilter?.children?.length > 0 || reduxViewState?.advancedFilters?.itemLevelCd)
  );
  const isLevelAgg = isAFActive && !!advancedFilters?.itemLevelCd;
  const [openFilter, setOpenFilter] = useState(false);
  const [sortParams, setSortParams] = useState({});
  const [rpResults, setRpResults] = useState([]);
  const [filterData, setFilterData] = useState({});
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(true);
  const [noteUpdateFlg, setNoteUpdateFlg] = useState(false);
  const [updateNoteData, setUpdateNoteData] = useState({});
  const [noteCellData, setNoteCellData] = useState({});

  // item attribute
  const [dimenModalOpen, setDimenModalOpen] = useState(false);
  const [dimensionAttrData, setDimensionAttrData] = useState(reduxViewState?.dimensionAttrData ?? {});
  const [attrConfig, setAttrConfig] = useState(null);

  const gridRef = useRef();
  const columnDefs = useRef();
  const exportColumnDefs = useRef();
  const noteKeySet = useRef(new Set());

  const [columnApi, setColumnApi] = useState(null);
  const [gridLoading, setGridLoading] = useState(false);
  const [detailColumnDefs, setDetailColumnDefs] = useState([]);
  const [kitDetailColumnDefs, setKitDetailColumnDefs] = useState([]);
  const [kitRpOrderDetailColumnDefs, setKitRpOrderDetailColumnDefs] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);
  const [p13Measures, setP13Measures] = useState([]);
  const allMeasure = CodeUtil.rpReviewMeasures;
  const defaultMeasure = CodeUtil.defaultRpReviewMeasures;
  const [displayName, setDisplayName] = useState(reduxViewState?.displayName ?? null);
  const gridHeaderMap = useRef({});
  const chartSelectedRowsRef = useRef([]); // 마지막 차트 선택 행 — 언어 변경 시 재조립용
  const localDateHeaderMap = useRef([]);

  const [chartHeaders, setChartHeaders] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartLabel, setChartLabel] = useState([]);
  const [initLoad, setInitLoad] = useState(false);
  const [orderByItem, setOrderByItem] = useState(reduxViewState?.orderByItem ?? null);
  const initSelectionDoneRef = useRef(false);

  const [layoutModalData, setLayoutModalData] = useState(reduxViewState?.layoutModalData ?? {});
  const [layoutOpen, setLayoutOpen] = useState(false);
  const layoutLoad = useRef(false);
  const [itemCustomValue, setItemCustomValue] = useState(reduxViewState?.filter?.category ?? []);
  const [treeLoad, setTreeLoad] = useState(false);
  const [initialFlg, setInitialFlg] = useState(reduxViewState?.initialFlg ?? false);
  const [p13nColumnState, setP13nColumnState] = useState([]);
  const MODEL_CODE = item.name;

  const [thresholdModalOpen, setThresholdModalOpen] = useState(false);
  const [invCovDaysConfig, setInvCovDaysConfig] = useState({});

  const userData = JSON.parse(localStorage.getItem("user"));
  const currencyMap = useRef({});
  const defaultTypeOptions = { strokeWidth: 1.5, barSize: 20 };

  const getDisplayText = (translationKey, originTxt) => {
    if (displayName != null && displayName[translationKey]) {
      return displayName[translationKey];
    }
    return t(originTxt);
  };

  const chartSeries = [
    { field: PURCHASE_PLAN_QTY, text: getDisplayText(PURCHASE_PLAN_QTY, "purchasePlanQty"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: REPLENISH_PLAN_QTY, text: getDisplayText(REPLENISH_PLAN_QTY, "replenishPlanQty"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: DEMAND_QTY, text: getDisplayText(DEMAND_QTY, "demandQty"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: KIT_FCST_DEMAND_QTY, text: getDisplayText(KIT_FCST_DEMAND_QTY, "kitFcstDemandQty"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: FCST_DEMAND_QTY, text: getDisplayText(FCST_DEMAND_QTY, "fcstDemandQty"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: INTERNAL_DEMAND_QTY, text: getDisplayText(INTERNAL_DEMAND_QTY, "internalDemandQty"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: DOWNSTREAM_DEMAND_QTY_SUM, text: getDisplayText(DOWNSTREAM_DEMAND_QTY_SUM, "downstreamDemandQtySum"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: DOWNSTREAM_EOH_QTY_SUM, text: getDisplayText(DOWNSTREAM_EOH_QTY_SUM, "downstreamEohQtySum"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: BOH, text: getDisplayText(BOH, "boh"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: EOH, text: getDisplayText(EOH, "eoh"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: TARGET_STOCK, text: getDisplayText(TARGET_STOCK, "targetStock"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: DEMAND_AMT, text: getDisplayText(DEMAND_AMT, "demandAmt"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt") },
    { field: FCST_DEMAND_AMT, text: getDisplayText(FCST_DEMAND_AMT, "fcstDemandAmt"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt"), isAmt: true },
    { field: KIT_FCST_DEMAND_AMT, text: getDisplayText(KIT_FCST_DEMAND_AMT, "kitFcstDemandAmt"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt"), isAmt: true },
    { field: INTERNAL_DEMAND_AMT, text: getDisplayText(INTERNAL_DEMAND_AMT, "internalDemandAmt"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt"), isAmt: true },
    { field: DOWNSTREAM_DEMAND_AMT_SUM, text: getDisplayText(DOWNSTREAM_DEMAND_AMT_SUM, "downstreamDemandAmtSum"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt"), isAmt: true },
    { field: DOWNSTREAM_EOH_AMT_SUM, text: getDisplayText(DOWNSTREAM_EOH_AMT_SUM, "downstreamEohAmtSum"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt"), isAmt: true },
    { field: BOH_AMT, text: getDisplayText(BOH_AMT, "bohAmt"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt"), isAmt: true },
    { field: ARRIVAL_QTY, text: getDisplayText(ARRIVAL_QTY, "arrivalQty"), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: ARRIVAL_QTY_PLAN, text: getDisplayText(ARRIVAL_QTY_PLAN, "arrivalQtyPlan"), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: ARRIVAL_QTY_CFM, text: getDisplayText(ARRIVAL_QTY_CFM, "arrivalQtyCfm"), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: ARRIVAL_AMT, text: getDisplayText(ARRIVAL_AMT, ARRIVAL_AMT), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt"), isAmt: true },
    { field: SHIPMENT_QTY, text: getDisplayText(SHIPMENT_QTY, SHIPMENT_QTY), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SHIPMENT_QTY_PLAN, text: getDisplayText(SHIPMENT_QTY_PLAN, SHIPMENT_QTY_PLAN), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SHIPMENT_QTY_CFM, text: getDisplayText(SHIPMENT_QTY_CFM, SHIPMENT_QTY_CFM), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SHIPMENT_AMT, text: getDisplayText(SHIPMENT_AMT, SHIPMENT_AMT), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt") },
    { field: EOH_AMT, text: getDisplayText(EOH_AMT, EOH_AMT), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt"), isAmt: true },
    { field: TARGET_STOCK_AMT, text: getDisplayText(TARGET_STOCK_AMT, TARGET_STOCK_AMT), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt"), isAmt: true },
    { field: ROP, text: getDisplayText(ROP, ROP), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: REPLENISH_PLAN_AMT, text: getDisplayText(REPLENISH_PLAN_AMT, REPLENISH_PLAN_AMT), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt"), isAmt: true },
    { field: PURCHASE_PLAN_AMT, text: getDisplayText(PURCHASE_PLAN_AMT, PURCHASE_PLAN_AMT), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt"), isAmt: true },
    { field: EXP_INV_QTY, text: getDisplayText(EXP_INV_QTY, EXP_INV_QTY), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: EXP_INV_AMT, text: getDisplayText(EXP_INV_AMT, EXP_INV_AMT), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("amt"), isAmt: true },
    { field: BACKORDER_QTY, text: getDisplayText(BACKORDER_QTY, BACKORDER_QTY), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: INV_COV_DAYS, text: getDisplayText(INV_COV_DAYS, INV_COV_DAYS), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SHIPMENT_SHORTAGE_QTY, text: getDisplayText(SHIPMENT_SHORTAGE_QTY, SHIPMENT_SHORTAGE_QTY), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") }
  ];

  if (rpVersionFilter?.planConfigObj?.USE_MP_PRODUCTION_PLAN && itemTypeSearch !== "FG") {
    chartSeries.push({ field: PROD_QTY, text: getDisplayText(PROD_QTY, PROD_QTY), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") });
  }

  const pivotConfig = {
    groupKey: "measure",
    chartSeries,
    include: chartLabel,
    xAxisSortMap: gridHeaderMap.current
  };

  const handleDimenClose = () => {
    setDimenModalOpen(false);
  };


  const getRowStyle = params => {
    if(!isEmpty(params?.node?.groupData)) {
      return { background: green[50] };
    }

    if (params.node.firstChild) {
      return { background: green[50] };
    }

    return null;
  }

  const validateCautionTarget = (invCovDays) => {
    if (!isEmpty(invCovDaysConfig) && invCovDaysConfig[INV_COV_DAYS_APPLY]) {
      if (invCovDaysConfig[INV_COV_DAYS_COMPUTED_METHOD] === "lessThan") {
        return invCovDays < invCovDaysConfig[INV_COV_DAYS_THRESHOLD];
      } else {
        return invCovDays > invCovDaysConfig[INV_COV_DAYS_THRESHOLD];
      }
    }

    return false;
  }

  const findMeasureData = (nodes, measure) => {
    for (const node of nodes) {
      if (isEqual(node.data.originMeasure, measure)) {
        return node?.data;
      }
    }

    return {};
  }

  const getIsException = (params, headers) => {
    const { column, data, value } = params;

    if (isNull(data) || isNull(value)) {
      return false;
    }

    const measure = data?.originMeasure;
    if (!measure) {
      return false;
    }

    const locationItemNodes = params.node?.parent?.allLeafChildren;

    if (isEmpty(locationItemNodes)) {
      return false;
    }

    const numValue = Number(value);
    const replacePostFix = rpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + rpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
    const replaceColId = column?.colId.replace(replacePostFix, "");
    const headerIndex = headers.indexOf(replaceColId);

    if (headerIndex !== -1 && value !== null) {
      const demandData = findMeasureData(locationItemNodes, DEMAND_QTY);
      const demandQty = demandData[headers[headerIndex]];
      return isEqual(measure, SHIPMENT_QTY) && demandQty > numValue;
    }

    return false;
  }

  const cellStyle = (params, headers) => {
    const { column, data, value } = params;
    const cellStyle = {};

    if (!isNull(data) && !isNull(value)) {
      const isException = getIsException(params, headers);
      if (isException) {
        cellStyle.background = red[100];
        cellStyle.fontStyle = 'italic';
      }

      if (data?.originMeasure && isEqual(data.originMeasure, EOH) && value < 0) {
        cellStyle.color = red[500];
      }

      if (data?.originMeasure && isEqual(data.originMeasure, INV_COV_DAYS) && validateCautionTarget(value)) {
        cellStyle.color = red[500];
      }

      if (noteKeySet.current.size) {
        const replacePostFix = rpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + rpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
        const replaceColId = column?.colId.replace(replacePostFix, "");

        if (!data) return { };

        const noteKey = CodeUtil.getNoteKey(viewName, {
          originLocCd: data.originLocCd, destLocCd: data.destLocCd, itemCd: data.itemCd, baseDate: replaceColId,
          originMeasure: NOTE_SIM_MEASURE[data.originMeasure] ?? data.originMeasure, measure: data.measure
        });
        if (noteKeySet.current.has(noteKey)) {
          cellStyle.borderLeft = "3px solid #4caf50";
        }
      }
    }

    return cellStyle;
  }

  // 같은 도착지/품목 중 현재 보이는 첫 컬럼만 노트 마커를 담당한다
  const getNoteMarkerColId = (api, colId) => {
    const markerColumns = NOTE_MARKER_COLUMNS.find(columns => columns.includes(colId));
    if (!markerColumns) {
      return null;
    }
    return markerColumns.find(column => api?.getColumn(column)?.isVisible()) ?? null;
  }

  const isFirstRowOfGroup = (node, groupField) => {
    let group = node?.parent;
    while (group && group.field !== groupField) group = group.parent;

    const leaves = group?.allLeafChildren;
    if (isEmpty(leaves)) {
      return false;
    }

    let firstIndex = Number.MAX_SAFE_INTEGER;
    for (const leaf of leaves) {
      if (!isNull(leaf.rowIndex) && leaf.rowIndex < firstIndex) firstIndex = leaf.rowIndex;
    }
    return node.rowIndex === firstIndex;
  }

  const locItemNoteCellStyle = (params) => {
    const { data, node, api, column } = params;
    if (!data || !noteKeySet.current.size) {
      return {};
    }

    const colId = column?.getColId();
    if (getNoteMarkerColId(api, colId) !== colId) {
      return {};
    }

    const noteKey = CodeUtil.getNoteKey(viewName, { originLocCd: data.originLocCd, destLocCd: data.destLocCd, itemCd: data.itemCd });
    if (!noteKeySet.current.has(noteKey)) {
      return {};
    }

    return isFirstRowOfGroup(node, NOTE_MARKER_GROUP_FIELD[colId]) ? { borderLeft: "3px solid #4caf50" } : {};
  }

  const emptyValueFormatter = (params, isExport = false) => {
    const { data, colDef } = params;
    const destLocCd = data?.destLocCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[destLocCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      }
    }

    const replenishPlanQtyPivotData = "replenishPlanQtyPivotData";
    const purchasePlanQtyPivotData = "purchasePlanQtyPivotData";
    const rpAmtArray = ["purchasePlanAmt", "replenishPlanAmt"];

    let value = data?.[colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    if (Object.is(value, -0)) {
      value = 0;
    }

    let formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
    const measure = data?.originMeasure ?? data?.measure;

    if (measure?.includes("Amt")) {
      if (rpAmtArray.indexOf(measure) !== -1) {
        const pivotDataKey = measure === "purchasePlanAmt" ? purchasePlanQtyPivotData : replenishPlanQtyPivotData;
        const hasNullValue = data?.[pivotDataKey]?.some(element => element === null);

        if (hasNullValue) {
          return "";
        }
      }
      formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
      if (isExport) {
        return formatValue.replace(/,/g, "");
      }
      return value >= 0 ? symbol + formatValue : `-${symbol}${formatValue.replace("-", "")}`;
    }

    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    return formatValue;
  }
  emptyValueFormatter.customType = EXCEL_EXPORT;

  const itemTypeValueFormatter = params => {
    const { value } = params;

    return itemType?.[value] ? GridUtils.gridValueL10N(itemType[value]?.name) : value;
  };

  const noteDefs = [
    { headerName: t("originLocation"), field: "originLocCd", sort: "asc", hide: false, cellClass: 'stringType' },
    { headerName: t("destLocation"), field: "destLocCd", sort: "asc", hide: false, cellClass: 'stringType' },
    { headerName: t("item"), field: "itemKey", sort: "asc", hide: false, cellClass: 'stringType' },
    { headerName: t("date"), field: "date", sort: "asc", hide: false },
    { headerName: t("measure"), field: "measure", sort: "asc", hide: false, valueFormatter: (params) => t(params.value) },
    { headerName: t("notes"), field: "contentTxt", sort: "asc", hide: false },
  ];

  const defaultDetailColumnDefs = [
    { headerName: "originLocation", field: "originLocationName", width: 200 },
    { headerName: "destLocation", field: "destLocationName", width: 200 },
    { headerName: "measure", field: "measure", width: 160, valueFormatter: (params) => t(params.value) }
  ]

  const kitDefaultRpOrderQtyDetailColumnDefs = [
    { headerName: "originLocation", field: "originLocationName", width: 200 },
    { headerName: "destLocation", field: "destLocationName", width: 200 },
    { headerName: "itemCd", field: "itemCd", width: 150, cellClass: 'stringType' },
    { headerName: "itemName", field: "itemName", width: 200 },
    { headerName: "measure", field: "measure", width: 160, valueFormatter: (params) => t(params.value) }
  ]

  const kitDefaultDetailColumnDefs = [
    { headerName: "locCd", field: "destLocationName", width: 200 },
    { headerName: "itemCd", field: "itemCd", width: 150, cellClass: 'stringType' },
    { headerName: "itemName", field: "itemName", width: 200},
  ]

  const detailGrid = useMemo(()=> {
    return (params) => {
      const res ={};

      const replenishPlanQty = "replenishPlanQty";
      const replenishPlanQtyPivotData = "replenishPlanQtyPivotData";
      const purchasePlanQty = "purchasePlanQty";
      const purchasePlanQtyPivotData = "purchasePlanQtyPivotData";
      const bohQty = "boh";
      const bohQtyPivotData = "bohQtyPivotData";
      const arrivalQty = "arrivalQty";
      const arrivalQtyPivotData = "arrivalQtyPivotData";
      const arrivalQtyPlan = "arrivalQtyPlan";
      const arrivalQtyPlanPivotData = "arrivalQtyPlanPivotData";
      const arrivalQtyCfm = "arrivalQtyCfm";
      const arrivalQtyCfmPivotData = "arrivalQtyCfmPivotData";
      const eohQty = "eoh";
      const eohQtyPivotData = "eohQtyPivotData";

      const isKitItem = params.data.itemType === "KIT";

      res.getDetailRowData = (params) => {
        const rpOrderDetailDatas = params.data.rpOrderDetail || [];
        const parentMeasure = params.data.originMeasure;
        for (const data of rpOrderDetailDatas) {
          for (let idx = 0, len = chartHeaders.length; idx < len; idx++) {
            const header = chartHeaders[idx];
            if (isKitItem && params.data.originMeasure === bohQty) {
              data[header] = data[bohQtyPivotData][idx];
            }

            if (isKitItem && params.data.originMeasure === arrivalQty) {
              data[header] = data[arrivalQtyPivotData][idx];
            }

            if (isKitItem && params.data.originMeasure === eohQty) {
              data[header] = data[eohQtyPivotData][idx];
            }

            if (params.data.originMeasure === replenishPlanQty) {
              if (isKitItem) {
                const pivotDataMeasure = data.measure === replenishPlanQty ? replenishPlanQtyPivotData : purchasePlanQtyPivotData;
                data[header] = data[pivotDataMeasure][idx];
              } else {
                data[header] = data[replenishPlanQtyPivotData][idx];
              }
            } else if (params.data.originMeasure === purchasePlanQty) {
              data[header] = data[purchasePlanQtyPivotData][idx];
            }
          }
        }

        const rpDetailDatas = cloneDeep(rpOrderDetailDatas.filter((data) => data.measure === replenishPlanQty));
        const purchaseDetailDatas = cloneDeep(rpOrderDetailDatas.filter((data) => data.measure === purchasePlanQty));

        if (parentMeasure === replenishPlanQty) {
          if (isKitItem) {
            params.successCallback(rpOrderDetailDatas.filter(rpDetilData => {
              const pivotDataMeasure = rpDetilData.measure === replenishPlanQty ? replenishPlanQtyPivotData : purchasePlanQtyPivotData;
              return rpDetilData[pivotDataMeasure].reduce((acc, current) => acc + current || 0, 0) !== 0 }));
          } else {
            params.successCallback(rpDetailDatas.filter(rpDetilData => rpDetilData[replenishPlanQtyPivotData].reduce((acc, current) => acc + current || 0, 0) !== 0));
          }
        } else if (parentMeasure === purchasePlanQty) {
          params.successCallback(purchaseDetailDatas.filter(rpDetilData => rpDetilData[purchasePlanQtyPivotData].reduce((acc, current) => acc + current || 0, 0) !== 0));
        } else if (parentMeasure === bohQty) {
          params.successCallback(rpOrderDetailDatas.filter(rpDetilData => rpDetilData[bohQtyPivotData].reduce((acc, current) => acc + current || 0, 0) !== 0));
        } else if (parentMeasure === arrivalQty) {
          params.successCallback(rpOrderDetailDatas.filter(rpDetilData => rpDetilData[arrivalQtyPivotData].reduce((acc, current) => acc + current || 0, 0) !== 0));
        } else if (parentMeasure === arrivalQtyPlan) {
          params.successCallback(rpOrderDetailDatas.filter(rpDetilData => rpDetilData[arrivalQtyPlanPivotData].reduce((acc, current) => acc + current || 0, 0) !== 0));
        } else if (parentMeasure === arrivalQtyCfm) {
          params.successCallback(rpOrderDetailDatas.filter(rpDetilData => rpDetilData[arrivalQtyCfmPivotData].reduce((acc, current) => acc + current || 0, 0) !== 0));
        } else if (parentMeasure === eohQty) {
          params.successCallback(rpOrderDetailDatas.filter(rpDetilData => rpDetilData[eohQtyPivotData].reduce((acc, current) => acc + current || 0, 0) !== 0));
        }
      }

      if (params.data.originMeasure === replenishPlanQty || params.data.originMeasure === purchasePlanQty) {
        if (isKitItem) {
          if (isEmpty(filterValue?.category)) {
            res.detailGridOptions = {
              columnDefs: GridUtils.getColumnDefs(kitRpOrderDetailColumnDefs)
            }
          } else {
            const kitAggRpOrderDetailColumnsDefs = detailColumnDefs.filter(detailColumnDef => {
              return detailColumnDef.field !== "originLocationName";
            });
            res.detailGridOptions = {
              columnDefs: GridUtils.getColumnDefs(kitAggRpOrderDetailColumnsDefs)
            }
          }
        } else {
          res.detailGridOptions = {
            columnDefs: GridUtils.getColumnDefs(detailColumnDefs)
          }
        }
      } else {
        res.detailGridOptions = {
          columnDefs: GridUtils.getColumnDefs(kitDetailColumnDefs)
        }
      }

      return res;
    }
    // detailGridOptions 는 setColumnDefs 를 타지 않아 GridUtils 의 언어 변경 재적용 대상이
    // 아니다. 여기서 getColumnDefs 로 번역하므로 t 가 바뀌면 다시 만들어야 한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartHeaders, detailColumnDefs, kitDetailColumnDefs, kitRpOrderDetailColumnDefs, t]);

  const detailEnabled = (e) => {
    return (isEqual(e.itemType,"KIT") && isEqual(e.originMeasure, REPLENISH_PLAN_QTY)) || (isEqual(e.itemType,"KIT") && isEqual(e.originMeasure, BOH) && !e.isAggregationRow) || (isEqual(e.itemType,"KIT") && isEqual(e.originMeasure, ARRIVAL_QTY) && !e.isAggregationRow) || (isEqual(e.itemType,"KIT") && isEqual(e.originMeasure, EOH) && !e.isAggregationRow);
  }

  const cellRendererParams = (e) => {
    const measure = e.data?.originMeasure;
    if (isEqual(measure, REPLENISH_PLAN_QTY) || isEqual(measure, PURCHASE_PLAN_QTY)  || isEqual(measure, BOH) || isEqual(measure, ARRIVAL_QTY) || isEqual(measure, EOH)) {
      e.node.setExpanded(false);
    }
    return { suppressCount: true }
  }

  const translationFormatter = params => {
    const { data, colDef } = params;
    const value = data?.[colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    return t(value);
  }

  const checkboxRender = (params) => {
    return measures && measures.length > 0 && params.data?.measure === measures[0];
  }

  const getAttributeColDefs = (isCategory, isExcel = false) => {
    const itemColDefs = [];
    const attrDatas = dimensionAttrData?.attr;

    if (isCategory && !attrDatas) {
      return itemColDefs;
    }

    for (let i = 0; i < attrDatas?.length; i ++) {
      const attrData = attrDatas[i];
      const attrId = `prod_${attrData}`;
      const sort = isEqual(sortParams?.orderByColumn, attrId) ? sortParams?.sortType : null;
      const attrNm = attrConfig?.item ? attrConfig.item[attrData] ?? attrData : attrData;
      const itemAttr = "itemAttr";
      const fieldNm = itemAttr + i;

      const itemAttrArry = isExcel ? [
        {
          headerName: attrNm,
          colId: attrId,
          field: fieldNm,
          cellClass: "stringType",
          width: 120,
        },
        {
          field: fieldNm,
          editable: false,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
        }] : [{
        headerName: attrNm,
        colId: attrId,
        showRowGroup: fieldNm,
        chartDataType: "category",
        pinned: "left", lockPinned: true,
        cellRenderer: "agGroupCellRenderer",
        cellClass: "stringType",
        cellRendererParams: {
          suppressCount: true,
        },
        width: 120,
        sortable: true,
        sort
      },
        {
          field: fieldNm,
          editable: false,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
          sort
        }
      ];
      itemColDefs.push(...itemAttrArry);
    }
    return itemColDefs;
  }

  const defaultColumnDefs = useMemo(()=> {
    if (orderByItem === null) {
      return [];
    }

    const isCategory = !isEmpty(filterValue?.category) || isLevelAgg;
    const baseColumns = [
      {
        headerName: 'itemType', colId: "itemTypeGrp", width: 160, showRowGroup: 'itemType', pinned: "left", lockPinned: true, valueFormatter: itemTypeValueFormatter,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
      },
      { headerName: "itemType", field: "itemType", rowGroup: true, hide: true, suppressColumnsToolPanel: true, valueFormatter: itemTypeValueFormatter },
      isCategory ? {} : {
        headerName: INV_CLASS_CD, colId: "invClassCdGrp", width: 120, showRowGroup: INV_CLASS_CD, pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: { suppressCount: true, innerRenderer: AbcXyzChipInline }
      },
      isCategory ? {} : {
        headerName: INV_CLASS_CD, field: INV_CLASS_CD, rowGroup: true, hide: true, suppressColumnsToolPanel: true,
      },
      {
        headerName: "measure", field: "measure", width: 200, type: ["nonEditableColumn"], pinned: "left", lockPinned: true,
        valueFormatter: (params) => t(params.value), headerComponent: CustomHeaderMenuIcon,
        cellRenderer: 'agGroupCellRenderer', cellRendererParams,
        headerComponentParams: {
          enableMenu: false,
          isClicked: setMeasureModalOpen
        }
      }
    ];

    let defaultColoumns = [];

    const attributeColDefs = isCategory ? {} :{
      // column header dimension modal
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isSorting: true,
        isClicked: () => {
          setDimenModalOpen(true);
        }
      },
    };

    const itemColDefs = getAttributeColDefs(isCategory);

    if (orderByItem) {
      defaultColoumns = [
        {
          headerName: isCategory ? 'categoryCd' : 'itemCd', colId: "itemCdGrp", width: 160, showRowGroup: 'itemCd', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellClass: 'stringType',
          cellRendererParams: { suppressCount: true, checkbox: checkboxRender }, cellStyle: locItemNoteCellStyle
          ,...attributeColDefs
        },
        {
          headerName: isCategory ? 'categoryName' : 'itemName', colId: "itemNmGrp", width: 220, showRowGroup: 'itemNm', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }, cellStyle: locItemNoteCellStyle
        },
        { headerName: "itemCd", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        { headerName: "itemName", field: "itemNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        {
          headerName: 'originLocation', colId: "originLocNmGrp", width: 140, showRowGroup: 'originLocNm', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
        },
        {
          headerName: 'destLocation', colId: "destLocNmGrp", width: 140, showRowGroup: 'destLocNm', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }, cellStyle: locItemNoteCellStyle
        },
        { headerName: "originLocName", field: "originLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        { headerName: "destLocName", field: "destLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        ...baseColumns
      ];
    } else {
      defaultColoumns = [
        {
          headerName: 'originLocation', colId: "originLocNmGrp", width: 140, showRowGroup: 'originLocNm', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
        },
        {
          headerName: 'destLocation', colId: "destLocNmGrp", width: 140, showRowGroup: 'destLocNm', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }, cellStyle: locItemNoteCellStyle
        },
        { headerName: "originLocName", field: "originLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        { headerName: "destLocName", field: "destLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        {
          headerName: isCategory ? 'categoryCd' : 'itemCd', colId: "itemCdGrp", width: 160, showRowGroup: 'itemCd', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellClass: 'stringType',
          cellRendererParams: { suppressCount: true, checkbox: checkboxRender }, cellStyle: locItemNoteCellStyle
          ,...attributeColDefs
        },
        {
          headerName: isCategory ? 'categoryName' : 'itemName', colId: "itemNmGrp", width: 220, showRowGroup: 'itemNm', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }, cellStyle: locItemNoteCellStyle
        },
        { headerName: "itemCd", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        { headerName: "itemName", field: "itemNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        ...baseColumns
      ];
    }

    if (!isCategory) {
      const itemIdx = defaultColoumns.findIndex(col => isEqual(col.field, INV_CLASS_CD));
      defaultColoumns.splice(itemIdx + 1, 0, ...itemColDefs);
    }

    return GridUtils.getColumnDefs(defaultColoumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderByItem, filterValue, dimensionAttrData, isAFActive]);

  const defaultExportColumnDefs = useMemo(() => {
    let defaultColoumns = [];
    const isCategory = !isEmpty(filterValue?.category) || isLevelAgg;
    const itemColDefs = getAttributeColDefs(isCategory, true);
    const isKit = itemTypeSearch === CodeUtil.itemType.KIT.cd;

    if (orderByItem) {
      defaultColoumns = [
        { headerName: isCategory ? 'categoryCd' : 'itemCd', field: "itemCd", width: 140, cellClass: 'stringType' },
        { headerName: isCategory ? 'categoryName' : 'itemName', field: "itemNm", width: 140 },
        { field: "itemCd", colId: "itemCdGrp", rowGroup: true, hide: true },
        { field: "itemNm", colId: "itemNmGrp", rowGroup: true, hide: true },
        { field: "originLocNm", colId: "originLocNmGrp", rowGroup: true, hide: true },
        { field: "destLocNm", colId: "destLocNmGrp", rowGroup: true, hide: true },
        isKit ? null : { headerName: "originLocation", field: "originLocNm", width: 140 },
        { headerName: "destLocation", field: "destLocNm", width: 140 },
        { field: "itemType", colId: "itemTypeGrp", rowGroup: true, hide: true },
        { headerName: "itemType", field: "itemType", width: 160 },
        isCategory ? {} : { field: INV_CLASS_CD, colId: "invClassCdGrp", rowGroup: true, hide: true },
        isCategory ? {} : { headerName: INV_CLASS_CD, field: INV_CLASS_CD, width: 120, cellClass: 'stringType', cellRenderer: AbcXyzChip },
        { headerName: "measure", field: "measure", width: 160, valueGetter: translationFormatter},
        { headerName: "isException", field: "isException", width: 100 }
      ].filter(col => col !== null);
    } else {
      defaultColoumns = [
        isKit ? null : { field: "originLocNm", colId: "originLocNmGrp", rowGroup: true, hide: true },
        { field: "destLocNm", colId: "destLocNmGrp", rowGroup: true, hide: true },
        isKit ? null : { headerName: "originLocation", field: "originLocNm", width: 140 },
        { headerName: "destLocation", field: "destLocNm", width: 140 },
        { field: "itemCd", colId: "itemCdGrp", rowGroup: true, hide: true },
        { field: "itemNm", colId: "itemNmGrp", rowGroup: true, hide: true },
        { headerName: isCategory ? 'categoryCd' : 'itemCd', field: "itemCd", width: 140, cellClass: 'stringType' },
        { headerName: isCategory ? 'categoryName' : 'itemName', field: "itemNm", width: 140 },
        { field: "itemType", colId: "itemTypeGrp", rowGroup: true, hide: true },
        { headerName: "itemType", field: "itemType", width: 160 },
        isCategory ? {} : { field: INV_CLASS_CD, colId: "invClassCdGrp", rowGroup: true, hide: true },
        isCategory ? {} : { headerName: INV_CLASS_CD, field: INV_CLASS_CD, width: 120, cellClass: 'stringType', cellRenderer: AbcXyzChip },
        { headerName: "measure", field: "measure", width: 160, valueGetter: translationFormatter },
        { headerName: "isException", field: "isException", width: 100 }
      ].filter(col => col !== null);
    }

    if (!isCategory) {
      const itemIdx = defaultColoumns.findIndex(col => isEqual(col.headerName, INV_CLASS_CD));
      defaultColoumns.splice(itemIdx + 1, 0, ...itemColDefs);
    }
    return GridUtils.getColumnDefs(defaultColoumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderByItem, filterValue, dimensionAttrData, itemTypeSearch, isAFActive]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const advancedFilterColumnDefs = [
    { headerName: "originLocation", filterType: "string" },
    { headerName: "destLocation", filterType: "string" },
    { headerName: "categoryCd", filterType: "string" },
    { headerName: "categoryName", filterType: "string" },
    { headerName: "itemCd", filterType: "string" },
    { headerName: "itemName", filterType: "string" },
    { headerName: "invClassCd", filterType: "string", filterOption: dpABCConfig },
    ...Array.from({ length: 10 }, (_, idx) => {
      const order = idx + 1;
      const attrCd = `attr${String(order).padStart(2, "0")}`; // attr01 ~ attr10 (= RpColumn headerName)
      return {
        headerName: attrConfig?.item?.[attrCd] ?? `attr${order}`, // 설정된 라벨, 없으면 attr1~10
        filterType: "string",
        filterName: attrCd,
      };
    }),
    { headerName: "excludeCondition", filterType: "select", filterOption: ["zeroReplenishment", "zeroPurchase", "zeroDemand", "zeroEoh", "exceptionsOnly"] },
  ].filter(Boolean);

  const ITEM_SEARCH_TO_AF = {
    itemCd: "itemCd",
    name: "itemName",
    itemHrchy: "categoryName",
    invClassCd: "invClassCd",
    ...Object.fromEntries(Array.from({ length: 10 }, (_, idx) => {
      const attrCd = `attr${String(idx + 1).padStart(2, "0")}`;
      return [attrCd, attrCd];
    })),
  };


  const buildInitialFromCurrentFilters = () => {
    const nodes = [];
    const originLocs = filterValue?.originLocation ? filterValue?.originLocation?.map(location => location?.name).filter(Boolean) : [];
    const destLocs = filterValue?.destLocation ? filterValue?.destLocation?.map(location => location?.name).filter(Boolean) : [];
    const exceptions = [...(appliedExceptions ?? [])];

    if (onlyExceptionApplied && !exceptions.includes("exceptionsOnly")) {
      exceptions.push("exceptionsOnly");
    }

    if (originLocs.length > 1) {
      nodes.push({ logicOperator: "OR", children: originLocs.map(name => ({ filterType: "string", headerName: "originLocation", operator: "equals", values: [name] })) });
    } else if (originLocs.length === 1) {
      nodes.push({ filterType: "string", headerName: "originLocation", operator: "equals", values: [originLocs[0]] });
    }

    if (destLocs.length > 1) {
      nodes.push({ logicOperator: "OR", children: destLocs.map(name => ({ filterType: "string", headerName: "destLocation", operator: "equals", values: [name] })) });
    } else if (destLocs.length === 1) {
      nodes.push({ filterType: "string", headerName: "destLocation", operator: "equals", values: [destLocs[0]] });
    }

    const [itemLogic, itemCompare] = itemFilterType ?? [filterType.or.cd, filterType.equal.cd];
    const itemOperator = itemCompare === filterType.notEqual.cd ? "notEqual" : "equals";
    const itemLogicOperator = itemLogic === filterType.and.cd ? "AND" : "OR";
    const unsupportedItemKeys = [];
    const itemNodes = [];

    for (const [key, values] of Object.entries(itemFilterList ?? {})) {
      const targets = (values ?? []).filter(Boolean);
      if (targets.length === 0) {
        continue;
      }
      const headerName = ITEM_SEARCH_TO_AF[key];
      if (!headerName) {
        unsupportedItemKeys.push(key);
        continue;
      }
      const leaf = value => ({ filterType: "string", headerName, operator: itemOperator, values: [value] });
      if (targets.length > 1) {
        itemNodes.push({ logicOperator: itemLogicOperator, children: targets.map(leaf) });
      } else {
        itemNodes.push(leaf(targets[0]));
      }
    }

    if (itemNodes.length > 1) {
      nodes.push({ logicOperator: itemLogicOperator, children: itemNodes });
    } else if (itemNodes.length === 1) {
      nodes.push(itemNodes[0]);
    }

    if (invClassCdList.length > 1) {
      nodes.push({ logicOperator: "OR", children: invClassCdList.map(cd => ({ filterType: "string", headerName: "invClassCd", operator: "equals", values: [cd] })) });
    } else if (invClassCdList.length === 1) {
      nodes.push({ filterType: "string", headerName: "invClassCd", operator: "equals", values: [invClassCdList[0]] });
    }

    for (const exception of exceptions) {
      nodes.push({ filterType: "select", headerName: "excludeCondition", filterOption: ["zeroReplenishment", "zeroPurchase", "zeroDemand", "zeroEoh", "exceptionsOnly"], operator: "equals", values: [exception] });
    }

    const categories = isArray(filterValue?.category) ? filterValue.category : (filterValue?.category ? [filterValue.category] : []);
    if (categories.length > 1) {
      nodes.push({ logicOperator: "OR", children: categories.map(cat => ({ filterType: "string", headerName: "categoryName", operator: "equals", values: [cat.name] })) });
    } else if (categories.length === 1) {
      nodes.push({ filterType: "string", headerName: "categoryName", operator: "equals", values: [categories[0].name] });
    }
    const filterTree = nodes.length > 0 ? { logicOperator: "AND", children: [{ logicOperator: "AND", children: nodes }] } : { logicOperator: "AND", children: [] };
    const detectedHrchyLv = categories.find(c => c?.hrchyLv && !c.leafFlg)?.hrchyLv ?? categories.find(c => c?.hrchyLv)?.hrchyLv;

    if (detectedHrchyLv) {
      return { advancedFilter: filterTree, itemLevelCd: detectedHrchyLv, itemType: itemTypeSearch, unsupportedItemKeys };
    }
    return { advancedFilter: filterTree, itemType: itemTypeSearch, unsupportedItemKeys };
  };

  const handleFilterOpen = () => {
    if (!openFilter) {
      if (!isAFActive) {
        const initial = buildInitialFromCurrentFilters();
        setPendingAdvancedFilters(initial)

        if (initial?.unsupportedItemKeys?.length > 0) {
          const fields = initial.unsupportedItemKeys.map(key => t(key)).join(", ");
          setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_UnsupportedItemFilterInAdvancedFilter", { fields }) });
        }
      } else {
        setPendingAdvancedFilters(null);
      }
      setNoteOpen(true);
    }
    setOpenFilter(prev => !prev)
  };

  const handleAdvancedFilterApply = (filters) => {
    const normalized = (filters?.advancedFilter?.children?.length > 0 || filters?.itemLevelCd || (filters?.itemType && filters?.itemType !== "FG")) ? filters : null;
    const clearedFilterValue = { originLocation: null, destLocation: null, category: null };
    setFilterValue(clearedFilterValue);
    setInvClassCdList([]);
    setAppliedExceptions([]);
    setOnlyExceptionApplied(false);
    setItemFilterList({});
    setItemCustomValue([]);
    setItemTypeSearch(filters?.itemType ?? 'FG');
    setAdvancedFilters(normalized);
    setPendingAdvancedFilters(normalized);
    setIsAFActive(!!normalized);
    layoutLoad.current = false;
    setLayoutModalData({});
    setMeasures(p13Measures);
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createFilterViewState(viewName, clearedFilterValue)));
  };


  const resetAdvancedFilter = () => {
    if (isAFActive) {
      setIsAFActive(false);
    }
    setPendingAdvancedFilters({ logicOperator: "AND", children: [] });
    setAdvancedFilters(null);
    setOpenFilter(false);
  };

  const handleItemTypeChange = (newValue) => {
    resetAdvancedFilter();
    setItemTypeSearch(newValue);
  };

  const handleFilterValueChange = (newValue) => {

    if (isAFActive && (newValue?.originLocation?.length > 0 || newValue?.destLocation?.length > 0 || newValue?.category?.length > 0)) {
      resetAdvancedFilter();
    }
    setFilterValue(newValue);
  };

  const handleInvClassCdListChange = (newList) => {
    resetAdvancedFilter();
    setInvClassCdList(newList);
  };

  const handleAppliedExceptionsChange = (newList) => {
    resetAdvancedFilter();
    setAppliedExceptions(newList);
  };

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (rpVersionFilter) {
      params.versionId = rpVersionFilter.id;
    }

    if (!isAFActive) {
      if (filterValue?.originLocation) {
        params.originLocCd = filterValue?.originLocation?.map(location => location?.locCd);
      }
      if (filterValue?.destLocation) {
        params.destLocCd = filterValue?.destLocation?.map(location => location?.locCd);
      }
      if (!isEmpty(filterValue?.category)) {
        params.categoryCds = isArray(filterValue?.category) ?
          filterValue?.category?.map(data => data?.hrchyCd) : [filterValue?.category?.hrchyCd];
      } else {
        if (itemFilterList) {
          params.itemFilterList = itemFilterList;
          params.itemFilterType = itemFilterType ?? [filterType.or.cd, filterType.equal.cd];
        }
      }
      if (!isEmpty(invClassCdList)) {
        params.invClassCdList = invClassCdList;
      }
      if (!isEmpty(appliedExceptions)) {
        params.appliedExceptions = appliedExceptions;
      }
    }

    if (itemTypeSearch) {
      params.itemType = itemTypeSearch;
    }

    if (isPagination) {
      params.page = currentPage ? currentPage - 1 : 0;
      params.pageSize = pageSize ? pageSize : 10;
    }

    if (!isEmpty(advancedFilters) && isAFActive) {
      const advancedFilter = advancedFilters?.advancedFilter;
      if (advancedFilters?.itemLevelCd) {
        params.itemLevelCd = advancedFilters.itemLevelCd;
      }
      if (!isEmpty(advancedFilter) && advancedFilter?.children?.length > 0) {
        params.advancedFilters = advancedFilter;
      }
    }

    if (!isEmpty(measures)) {
      params.measures = measures;
    } else if (!isEmpty(p13Measures)) {
      params.measures = p13Measures;
    } else {
      params.measures = allMeasure;
    }

    params.pagination = isPagination;
    params.totalCount = resultTotalCount.current ?? 0;
    params.currency = currency;
    params.orderByItem = orderByItem;
    //sortParams에는 존재하는데 header는 없을 때
    const sort = sortParams?.orderByColumn;
    const colIds = defaultColumnDefs?.map(colDef => colDef.colId);
    if (sort && !colIds.includes(sort)) {
      return { ...params, ...(isAFActive ? {} : { onlyExceptionApplied }) };
    }
    return { ...params, ...sortParams, ...(isAFActive ? {} : { onlyExceptionApplied }) };
  }

  const setGridHeaders = (headers, headerMap) => {
    if (headers.length <= 0) {
      return;
    }

    const colDefs = cloneDeep(defaultColumnDefs);
    const exportColDefs = cloneDeep(defaultExportColumnDefs);

    const detailColDefs = cloneDeep(defaultDetailColumnDefs);
    const kitDetailColDefs = cloneDeep(kitDefaultDetailColumnDefs);
    const kitRpOrderDetailColDefs = cloneDeep(kitDefaultRpOrderQtyDetailColumnDefs);

    columnApi?.setColumnsVisible(['originLocNmGrp'], itemTypeSearch === "FG");

    const bucketPostFix = rpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + rpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;

    headers.forEach((header) => {
      const displayHeader = headerMap[header];
      colDefs.push({
        headerName: displayHeader, field: header, width: 110, sortable: false,
        colId: header + bucketPostFix,
        type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        cellStyle: params => cellStyle(params, headers), valueFormatter: emptyValueFormatter
      });

      exportColDefs.push({
        headerName: displayHeader, field: header, width: 110,
        colId: header + bucketPostFix, valueFormatter: emptyValueFormatter,
        cellClassRules: {
          alertBackground: params => getIsException(params, headers),
        },
      });

      detailColDefs.push({
        headerName: displayHeader, field: header, width: 110, sortable: false,
        colId: header + bucketPostFix,
        // type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        valueFormatter: emptyValueFormatter
      });

      kitDetailColDefs.push({
        headerName: displayHeader, field: header, width: 110, sortable: false,
        colId: header + bucketPostFix,
        // type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        valueFormatter: emptyValueFormatter
      })

      kitRpOrderDetailColDefs.push({
        headerName: displayHeader, field: header, width: 110, sortable: false,
        colId: header + bucketPostFix,
        // type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        valueFormatter: emptyValueFormatter
      })
    });

    columnDefs.current = colDefs;
    exportColumnDefs.current = exportColDefs;
    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: colDefs });
    columnApi.applyColumnState({ state: p13nColumnState });

    setDetailColumnDefs(detailColDefs);
    setKitDetailColumnDefs(kitDetailColDefs);
    setKitRpOrderDetailColumnDefs(kitRpOrderDetailColDefs);
  }

  const getInvCovDays = (startIdx, headers, resultsData) => {
    const bucketType = rpVersionFilter?.planConfigObj?.BUCKET_UNIT;
    const bucketDays = bucketDaysObj[bucketType] || 1;

    const eohQtyPivotData = "eohQtyPivotData";
    const demandQtyPivotData = "demandQtyPivotData";

    let invCovDays = 0;
    let eoh = resultsData[eohQtyPivotData][startIdx];
    const endIdx = headers.length;

    for (let i = startIdx + 1; i < endIdx; i++) {
      const demandQty = resultsData[demandQtyPivotData][i];
      const leftStock = eoh - demandQty;

      if (leftStock >= 0) {
        eoh = leftStock;
        invCovDays += bucketDays;
      } else {
        const demandPerDay = Math.max(demandQty / bucketDays, 1);
        invCovDays += Math.floor(eoh / demandPerDay);
        break;
      }
    }

    return invCovDays;
  }

  const getShipmentShortageQty = (startIdx, headers, resultsData) => {
    const bohQtyPivotData = "bohQtyPivotData";
    const arrivalQtyCfmPivotData = "arrivalQtyCfmPivotData";
    const salesQtyPlanPivotData = "salesQtyPlanPivotData";

    const bucketType = rpVersionFilter?.planConfigObj?.BUCKET_UNIT;
    const planExecDate = rpVersionFilter?.planConfigObj?.PLAN_EXEC_DATE;
    const planExecBucket = RpUtil.getBucketDate(bucketType, planExecDate);

    if (!planExecBucket || headers.indexOf(planExecBucket) !== startIdx) {
      return 0;
    }

    const boh = resultsData[bohQtyPivotData][startIdx] ?? 0;
    const arrivalQtyCfm = resultsData[arrivalQtyCfmPivotData][startIdx] ?? 0;
    const salesQtyPlan = resultsData[salesQtyPlanPivotData][startIdx] ?? 0;

    return Math.max(0, salesQtyPlan - (boh + arrivalQtyCfm));
  }

  const setAttributeDatas = (resultsData) => {
    const itemAttr = "itemAttr";

    if (size(resultsData?.attributes) > 0) {
      for (let i = 0; i < resultsData.attributes.length; i++) {
        resultsData[itemAttr + i] = resultsData?.attributes?.[i];
      }
    }
  }

  const getIsExceptionData = (resultsData, headers) => {
    let isException = false;
    const demandQtyPivotData = "demandQtyPivotData";
    const salesQtyPivotData = "salesQtyPivotData";

    for (let idx = 0, len = headers.length; idx < len; idx++) {
      const demandVal = resultsData[demandQtyPivotData] ? resultsData[demandQtyPivotData][idx] : 0;
      const shipmentVal = resultsData[salesQtyPivotData] ? resultsData[salesQtyPivotData][idx] : 0;
      if (demandVal > shipmentVal) {
        isException = true;
      }
    }

    return isException;
  }

  const setGridDatas = (headers, results, measureCds = null) => {
    const datas = [];
    const demandQtyPivotData = "demandQtyPivotData";
    const demandAmtPivotData = "demandAmtPivotData";
    const fcstDemandQtyPivotData = "fcstDemandQtyPivotData";
    const fcstDemandAmtPivotData = "fcstDemandAmtPivotData";
    const replDemandQtyPivotData = "replDemandQtyPivotData";
    const replDemandAmtPivotData = "replDemandAmtPivotData";
    const downstreamDemandQtySumPivotData = "downstreamDemandQtySumPivotData";
    const downstreamDemandAmtSumPivotData = "downstreamDemandAmtSumPivotData";
    const downstreamEohQtySumPivotData = "downstreamEohQtySumPivotData";
    const downstreamEohAmtSumPivotData = "downstreamEohAmtSumPivotData";
    const kitFcstDemandQtyPivotData = "kitFcstDemandQtyPivotData";
    const kitFcstDemandAmtPivotData = "kitFcstDemandAmtPivotData";
    const bohQtyPivotData = "bohQtyPivotData";
    const bohAmtPivotData = "bohAmtPivotData";
    const arrivalQtyPivotData = "arrivalQtyPivotData";
    const arrivalQtyPlanPivotData = "arrivalQtyPlanPivotData";
    const arrivalQtyCfmPivotData = "arrivalQtyCfmPivotData";
    const arrivalAmtPivotData = "arrivalAmtPivotData";
    const salesQtyPivotData = "salesQtyPivotData";
    const salesQtyPlanPivotData = "salesQtyPlanPivotData";
    const salesQtyCfmPivotData = "salesQtyCfmPivotData";
    const salesAmtPivotData = "salesAmtPivotData";
    const salesQtyFcstPivotData = "salesQtyFcstPivotData";
    const salesAmtFcstPivotData = "salesAmtFcstPivotData";
    const salesQtyReplPivotData = "salesQtyReplPivotData";
    const salesAmtReplPivotData = "salesAmtReplPivotData";
    const prodQtyPivotData = "prodQtyPivotData";
    const eohQtyPivotData = "eohQtyPivotData";
    const eohAmtPivotData = "eohAmtPivotData";
    const targetStockQtyPivotData = "targetStockQtyPivotData";
    const targetStockAmtPivotData = "targetStockAmtPivotData";
    const reorderQtyPivotData = "reorderQtyPivotData";
    const replenishPlanQtyPivotData = "replenishPlanQtyPivotData";
    const replenishPlanAmtPivotData = "replenishPlanAmtPivotData";
    const purchasePlanQtyPivotData = "purchasePlanQtyPivotData";
    const purchasePlanAmtPivotData = "purchasePlanAmtPivotData";
    const expInvQtyPivotData = "expInvQtyPivotData";
    const expInvAmtPivotData = "expInvAmtPivotData";
    const backorderQtyPivotData = "backorderQtyPivotData";

    const measure = isNull(measureCds) ? isEmpty(measures) ? isEmpty(p13Measures) ? allMeasure : p13Measures : measures
      : measureCds;

    const useShipmentTxn = rpVersionFilter?.planConfigObj?.USE_SHIPMENT_TXN ?? false;

    for (const resultsData of results) {
      const isExceptionData = getIsExceptionData(resultsData, headers);
      for (const measureName of measure) {
        const display = displayName?.[measureName];
        const data = { ...resultsData,
          measure: display != null && !isEmpty(display) ? display : measureName,
          originMeasure: measureName,
          isException: isExceptionData };
        setAttributeDatas(data);

        if ((filterValue?.category && filterValue?.category?.length !== 0) || isLevelAgg) {
          data.isAggregationRow = true;
        }

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];

          switch (measureName) {
            case DEMAND_QTY:
              data[header] = resultsData[demandQtyPivotData][idx];
              break;

            case DEMAND_AMT:
              data[header] = resultsData[demandAmtPivotData][idx];
              break;

            case KIT_FCST_DEMAND_QTY:
              data[header] = resultsData[kitFcstDemandQtyPivotData][idx];
              break;

            case KIT_FCST_DEMAND_AMT:
              data[header] = resultsData[kitFcstDemandAmtPivotData][idx];
              break;

            case FCST_DEMAND_QTY:
              data[header] = resultsData[fcstDemandQtyPivotData][idx];
              break;

            case FCST_DEMAND_AMT:
              data[header] = resultsData[fcstDemandAmtPivotData][idx];
              break;

            case INTERNAL_DEMAND_QTY:
              data[header] = resultsData[replDemandQtyPivotData][idx];
              break;

            case INTERNAL_DEMAND_AMT:
              data[header] = resultsData[replDemandAmtPivotData][idx];
              break;

            case DOWNSTREAM_DEMAND_QTY_SUM:
              data[header] = resultsData[downstreamDemandQtySumPivotData][idx];
              break;

            case DOWNSTREAM_DEMAND_AMT_SUM:
              data[header] = resultsData[downstreamDemandAmtSumPivotData][idx];
              break;

            case DOWNSTREAM_EOH_QTY_SUM:
              data[header] = resultsData[downstreamEohQtySumPivotData][idx];
              break;

            case DOWNSTREAM_EOH_AMT_SUM:
              data[header] = resultsData[downstreamEohAmtSumPivotData][idx];
              break;

            case BOH:
              data[header] = resultsData[bohQtyPivotData][idx];
              break;

            case BOH_AMT:
              data[header] = resultsData[bohAmtPivotData][idx];
              break;

            case ARRIVAL_QTY:
              data[header] = resultsData[arrivalQtyPivotData][idx];
              break;

            case ARRIVAL_QTY_PLAN:
              data[header] = resultsData[arrivalQtyPlanPivotData][idx];
              break;

            case ARRIVAL_QTY_CFM:
              data[header] = resultsData[arrivalQtyCfmPivotData][idx];
              break;

            case ARRIVAL_AMT:
              data[header] = resultsData[arrivalAmtPivotData][idx];
              break;

            case SHIPMENT_QTY:
              data[header] = resultsData[salesQtyPivotData][idx];
              break;

            case SHIPMENT_QTY_PLAN:
              data[header] = resultsData[salesQtyPlanPivotData][idx];
              break;

            case SHIPMENT_QTY_CFM:
              data[header] = resultsData[salesQtyCfmPivotData][idx];
              break;

            case SHIPMENT_AMT:
              data[header] = resultsData[salesAmtPivotData][idx];
              break;

            case SHIPMENT_QTY_FCST:
              data[header] = resultsData[salesQtyFcstPivotData][idx];
              break;

            case SHIPMENT_AMT_FCST:
              data[header] = resultsData[salesAmtFcstPivotData][idx];
              break;

            case SHIPMENT_QTY_REPL:
              data[header] = resultsData[salesQtyReplPivotData][idx];
              break;

            case SHIPMENT_AMT_REPL:
              data[header] = resultsData[salesAmtReplPivotData][idx];
              break;

            case PROD_QTY:
              data[header] = resultsData[prodQtyPivotData][idx];
              break;

            case EOH:
              data[header] = resultsData[eohQtyPivotData][idx];
              break;

            case EOH_AMT:
              data[header] = resultsData[eohAmtPivotData][idx];
              break;

            case TARGET_STOCK:
              data[header] = resultsData[targetStockQtyPivotData][idx];
              break;

            case TARGET_STOCK_AMT:
              data[header] = resultsData[targetStockAmtPivotData][idx];
              break;

            case ROP:
              data[header] = resultsData[reorderQtyPivotData][idx];
              break;

            case REPLENISH_PLAN_QTY:
              data[header] = resultsData[replenishPlanQtyPivotData][idx];
              break;

            case REPLENISH_PLAN_AMT:
              data[header] = resultsData[replenishPlanAmtPivotData][idx];
              break;

            case PURCHASE_PLAN_QTY:
              data[header] = resultsData[purchasePlanQtyPivotData][idx];
              break;

            case PURCHASE_PLAN_AMT:
              data[header] = resultsData[purchasePlanAmtPivotData][idx];
              break;

            case EXP_INV_QTY:
              data[header] = resultsData[expInvQtyPivotData][idx];
              break;

            case EXP_INV_AMT:
              data[header] = resultsData[expInvAmtPivotData][idx];
              break;

            case BACKORDER_QTY:
              data[header] = resultsData[backorderQtyPivotData][idx];
              break;

            case INV_COV_DAYS:
              data[header] = getInvCovDays(idx, headers, resultsData);
              break;

            case SHIPMENT_SHORTAGE_QTY:
              data[header] = getShipmentShortageQty(idx, headers, resultsData);
              break;

            default:
              break;
          }
        }

        if (measureName === SHIPMENT_SHORTAGE_QTY && !useShipmentTxn) {
          continue;
        }

        if (measureName === PROD_QTY) {
          if (data.itemType === "FG" || !rpVersionFilter?.planConfigObj?.USE_MP_PRODUCTION_PLAN) {
            continue;
          }
        }

        if (data.itemType === "KIT") {
          if (salesKitMeasures.indexOf(measureName) > -1) {
            datas.push(data);
          }
        } else {
          datas.push(data);
        }
      }
    }

    return datas;
  }

  const removeNullVal = (params) => {
    for (const key of keys(params)) {
      if (isNull(params[key]) || isEqual(params[key], "")) delete params[key];
    }
    return params;
  }

  const getSortMeasures = (measureMap) => {
    if (!isEmpty(measureMap)) {
      const meaVals = values(measureMap);
      const sortVals = sortBy(meaVals, "seqNum");
      return sortVals.map(mea => mea.measureCd);
    }
    return [];
  }

  const getLayoutParams = (layoutData) => {
    const params = {};
    if (size(layoutData) > 2) {
      params.measures = getSortMeasures(layoutData?.activeMeasures);
      params.onlyExceptionApplied = layoutData?.exceptionsOnly ?? false;
    }

    return params;
  }

  useEffect(() => {
    if (!isEmpty(localDateHeaderMap.current) && !isEmpty(gridHeaderMap.current)) {
      setGridHeaders(localDateHeaderMap?.current, gridHeaderMap?.current);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invCovDaysConfig, rpResults])

  const retrieveRpReview = (layoutParams = {}, measureCds = null, layoutData = layoutModalData) => {
    initSelectionDoneRef.current = false;
    gridRef.current?.api?.setGridOption('loading', true);

    columnApi?.setColumnsVisible(['originLocNmGrp'], itemTypeSearch === "FG");
    // columnDefs.current = defaultColumnDefs;
    // exportColumnDefs.current = defaultExportColumnDefs;

    const params = getRequestParams();
    const finalParams = removeNullVal({ ...params, ...layoutParams });

    rpReviewService.getAll(finalParams)
      .then(response => {
        const { pivotResult, totalPages, totalItems, currency, noteResult } = response.data;
        const { localDateHeader, headerMap, data } = pivotResult;
        if (pivotResult?.data && !isEmpty(pivotResult.data)) {
          gridHeaderMap.current = headerMap;
          localDateHeaderMap.current = localDateHeader;
          setChartHeaders(localDateHeader);
          setGridHeaders(localDateHeader, headerMap);
          setRpResults(setGridDatas(localDateHeader, data, measureCds));
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          gridHeaderMap.current = {};
          localDateHeaderMap.current = [];
          setChartHeaders([]);
          setChartLabel([]);
          setChartData([]);
          setRpResults([]);
        }
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
        noteKeySet.current = new Set(noteResult ?? []);
        if (size(currency)) {
          currencyMap.current = currency;
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        let filter = {};
        if (layoutData?.reloadFalse && size(layoutData) < 2) {
          filter = filterValue;
        } else {
          // category 는 다중 선택 배열 — 레이아웃 적용 effect 와 같은 규칙으로 만든다(단일 객체를 넣으면 ItemHrchyTreeFilter 가 .map 에서 죽는다)
          filter = size(layoutData) > 2 ? {...filterValue, category: layoutData?.itemHrchy && layoutData?.itemHrchyObj ?
            layoutData.itemHrchyObj.filter(hrchy => includes(layoutData.itemHrchy, hrchy.hrchyCd)) : null,
            originLocation: layoutData?.originLocation ? Array.isArray(layoutData?.originLocation) ? layoutData?.originLocation : [layoutData?.originLocation] : null,
            destLocation: layoutData?.destLocation ? Array.isArray(layoutData?.destLocation) ? layoutData?.destLocation : [layoutData?.destLocation] : null } : filterValue;
        }
        const layoutParams = getLayoutParams(layoutData);
        const reduxState = reduxUtil.createObjectState(viewName, { itemTypeSearch, onlyExceptionApplied, layoutModalData: layoutData,
          measures, currentPage, pageSize, initialFlg, ...layoutParams, orderByItem, invClassCdList: size(layoutData) > 2 ? layoutData?.invClassCdList : invClassCdList,
          appliedExceptions: size(layoutData) > 2 ? layoutData?.appliedExceptions : appliedExceptions, dimensionAttrData, advancedFilters: isAFActive ? advancedFilters : null, displayName });
        const filterState = reduxUtil.createFilterViewState(viewName, filter);
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...filterState }));
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
        layoutLoad.current = false;
        setTreeLoad(false);
      });
  }

  const isClearLayout = () => {
    let clearFlg = false;

    const { originLocation, destLocation, activeMeasures, itemHrchy, itemType, exceptionsOnly } = layoutModalData;

    if (!isEqual(measures.sort(), keys(activeMeasures).sort())) {
      clearFlg = true;
    }

    const originLoc = !originLocation ? null : Array.isArray(originLocation) ? originLocation : [originLocation];
    const destLoc = !destLocation ? null : Array.isArray(destLocation) ? destLocation : [destLocation];

    if (!isEqual(originLoc?.map(val => val.locCd).sort(), filterValue?.originLocation?.map(val => val.locCd).sort())) {
      clearFlg = true;
    }
    if (!isEqual(destLoc?.map(val => val.locCd).sort(), filterValue?.destLocation?.map(val => val.locCd).sort())) {
      clearFlg = true;
    }

    const layoutCategoryCds = (isArray(itemHrchy) ? itemHrchy : (itemHrchy ? [itemHrchy] : [])).slice().sort();
    const currentCategoryCds = (isArray(filterValue?.category)
      ? filterValue.category.map(cat => cat?.hrchyCd)
      : (filterValue?.category?.hrchyCd ? [filterValue.category.hrchyCd] : [])).slice().sort();
    if (!isEqual(layoutCategoryCds, currentCategoryCds)) {
      clearFlg = true;
    }
    if (!isEqual(itemTypeSearch, itemType)) {
      clearFlg = true;
    }
    if (!isNull(exceptionsOnly) && exceptionsOnly !== onlyExceptionApplied) {
      clearFlg = true;
    }
    if (!isEqual(layoutModalData?.itemFilterType, itemFilterType)) {
      clearFlg = true;
    }
    if (!isEqual(layoutModalData?.itemFilterList, itemFilterList)) {
      clearFlg = true;
    }
    if (!isEqual((layoutModalData?.invClassCdList ?? []).slice().sort(), (invClassCdList ?? []).slice().sort())) {
      clearFlg = true;
    }
    if (!isEqual((layoutModalData?.appliedExceptions ?? []).slice().sort(), (appliedExceptions ?? []).slice().sort())) {
      clearFlg = true;
    }

    if (clearFlg) {
      setLayoutModalData({ reloadFalse: true });
      setMeasures(p13Measures);
      setCurrentPage(1);
      setTimeout(() => {
        retrieveRpReview({}, p13Measures, { reloadFalse: true });
      }, 100);
    } else {
      layoutLoad.current = false;
    }
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (size(layoutModalData) < 2) {
      if (currentPage > 1) {
        setCurrentPage(1);
      } else {
        retrieveRpReview();
      }
    } else {
      layoutLoad.current = true;
      isClearLayout();
    }
  }

  const retrieveP13n = () => {
    p13nService.getWithDisplayName(RP_REVIEW)
      .then(response => {
        const { data } = response;
        if (data && !isEmpty(data)) {
          const p13n = data[RP_REVIEW];
          if (p13n) {
            const p13nColumnState = p13n?.[gridId];
            setP13nColumnState(p13nColumnState);
          }
          const display = data["displayName"];
          if (display) {
            setDisplayName(display);
          }
          setOrderByItem(p13n?.orderByItem ?? false);
        } else {
          setOrderByItem(false);
        }

      })
      .catch(e => {
        debug(e);
      });
  }

  const getCustomConfig = () => {
    companyService.getCustomConfig()
      .then(response => {
        if (response.data) {
          setAttrConfig(response.data);
        } else {
          setAttrConfig({});
        }
      })
      .catch(e => {
        debug(e);
        setAttrConfig({});
      });
  };

  const retrieveAttributeConfig = () => {
    p13nService.get(`${viewName}_ETC`)
      .then(response => {
        if (response.data) {
          const attrConfig = response?.data?.ATTRIBUTE;
          setDimensionAttrData(attrConfig ? response.data.ATTRIBUTE : {});
        }
      })
      .catch(e => {
        debug(e);
        setDimensionAttrData({});
      });
  };

  const retrieveHrchyGroup = () => {
    hrchyConfigService.getAll()
      .then(response => {
        if (response.data) {
          const { results } = response.data;
          setItemGrpLevel(results.filter(lv => lv.itemHrchyFlg))
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const getDefaultCustomization = () => {
    const p13PageCd = `${viewName}_LAYOUT`;
    p13nService.getDefaultLayout(p13PageCd)
      .then(response => {
        const { data } = response;
        if (data?.layoutId) {
          setLayoutModalData(data);
        } else {
          retrieveRpReview({}, p13Measures, { reloadFalse: true });
        }
      })
      .catch(e => {
        initPageToRetrieve();
        debug(e);
      })
      .finally(() => {
        setInitialFlg(true);
        setInitLoad(true);
      })
  };

  const onGridReady = params => {
    setGridLoading(true);
    setColumnApi(params.api)
    GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true });
  }

  const applyMeasures = selectedMeasures => {
    setP13Measures(selectedMeasures);
    if (initialFlg) {
      setMeasures(selectedMeasures);
    }
  }

  /*
   * 표시명이 없으면(null/undefined/"") 측정치 코드 — setGridDatas 가 행의 measure 를 만드는 규칙과 같아야 한다.
   * RP Settings 를 한 번 저장한 테넌트는 표시명 설정이 {} 로 내려오는데, 예전 `!== null` 검사는 undefined 를
   * 통과시켜 차트 매칭 목록이 [undefined] 가 되고 차트가 비었다.
   */
  const getMeasureDisplayName = (measure) => {
    const display = displayName?.[measure];
    return display != null && !isEmpty(display) ? display : measure;
  }

  const displayNameMeasure = (measureList) => measureList.map(getMeasureDisplayName);

  const setChart = (selectedRows) => {
    const selectedDatas = [];
    const dateFormat = [];
    const activeHeaders = localDateHeaderMap.current?.length > 0 ? localDateHeaderMap.current : chartHeaders;

    chartSelectedRowsRef.current = selectedRows ?? [];

    if (selectedRows && selectedRows.length <= 0) {
      setChartData([]);
      return;
    }

    if (isEmpty(activeHeaders) || isEmpty(gridHeaderMap.current)) {
      setChartData([]);
      setChartLabel([]);
      return;
    }

    for (const header of activeHeaders) {
      dateFormat.push(gridHeaderMap.current[header]);
    }

    const measureList = isEmpty(measures) ? isEmpty(p13Measures) ? allMeasure : p13Measures : measures;
    const translationMeasure = displayNameMeasure(measureList)

    for (const row of selectedRows) {
      const chartDatas = {};
      let inputFlag = false;
      chartDatas.measure = t(row.measure);
      for (const header of activeHeaders) {
        if (translationMeasure?.includes(row.measure)) {
          const headerName = gridHeaderMap.current[header];
          chartDatas[headerName] = row[header];
          inputFlag = true;
        }
      }
      const destLocCd = row?.destLocCd;
      let symbol = userData?.currencySymbol;
      if (size(Object.keys(currencyMap?.current))) {
        const currencyCd = currencyMap.current[destLocCd];
        if (currencyCd) {
          symbol = CodeUtil.getCurrencySymbol(currencyCd);
        }
      }
      chartDatas.currency = symbol;
      if (inputFlag) {
        selectedDatas.push(chartDatas);
      }
    }
    setChartLabel(dateFormat);
    setChartData(selectedDatas);
    initSelectionDoneRef.current = true;
  }

  /*
   * 언어 변경 — 차트 데이터의 measure 값은 t() 로 번역된 문자열이고, PivotChartComposed 는 그 값을
   * 시리즈의 text 와 맞춰(chartSeries.find(text === dataKey)) 무엇을 그릴지 고른다. 시리즈만 새
   * 언어가 되면 짝이 깨져 그래프가 통째로 사라지므로, 데이터도 같이 다시 만든다(재조회 없음).
   */
  useEffect(() => {
    if (isEmpty(chartSelectedRowsRef.current)) {
      return;
    }
    setChart(chartSelectedRowsRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const onRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      const selectedRows = [];
      const selectedNodes = params.api.getSelectedNodes();

      const filterInfo = extractFilterInfo(params, t, gridRef);
      getFilterStore(viewName).setValue(filterInfo);

      const leafChildren = params.node.allLeafChildren ? params.node.allLeafChildren : params.node.parent.allLeafChildren
      for (const leaf of leafChildren) {
        selectedRows.push(leaf.data);
      }
      params.api.setNodesSelected({ nodes: selectedNodes, newValue: false });
      params.api.setNodesSelected({ nodes: leafChildren, newValue: true });
      setChart(selectedRows);
    }
  }

  const initCheck = (params) => {
    const rowCount = params.api.getDisplayedRowCount();
    if (rowCount === 0) {
      setChartData([]);
    } else {
      const firstNode = params.api.getDisplayedRowAtIndex(0);
      if (firstNode) {
        const selected = firstNode.allLeafChildren ? firstNode.allLeafChildren : firstNode.parent?.allLeafChildren ?? [];
        params.api.setNodesSelected({ nodes: selected, newValue: true, source: CHECKBOX_SELECTED });
      }
    }
  }

  const handleNoteModal = () => {
    setNoteOpen(!noteOpen);
    if (noteOpen) {
      if (openFilter) {
        setOpenFilter(false);
      }
    }
    if (!noteOpen && noteUpdateFlg) {
      setNoteUpdateFlg(false);
    }
  };

  const handleCheckbox = e => {
    const value = e.target.checked;
    resetAdvancedFilter();
    setOnlyExceptionApplied(value);
  }

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };

  const filterActionItem = params => {
    if (params.type === "click") {
      setFilterModalOpen(true);
    }
  };

  const onCellGridClicked = e => {
    const selected = e.node.allLeafChildren ? e.node.allLeafChildren : e.node.parent.allLeafChildren;
    e?.api?.setNodesSelected({nodes: selected, newValue: true, source: CHECKBOX_SELECTED});

    const replacePostFix = rpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + rpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
    const colId = e.column?.colId.replace(replacePostFix, "");
    if (!isEmpty(gridHeaderMap.current[colId])) {
      const matrix = [e.rowIndex, chartHeaders.indexOf(colId)];
      const target = e.data;
      const cellDate = colId;
      target.baseDate = cellDate;
      target.versionDto = rpVersionFilter;
      const cellData = {
        cellDate,
        timeDimension: rpVersionFilter.planConfigObj.BUCKET_UNIT,
        versionDto: {...rpVersionFilter},
        originMeasure: target.originMeasure,
        measure: target.measure,
        itemCd: target.itemCd,
        itemNm: target.itemNm,
        originLocCd: target.originLocCd,
        originLocNm: target.originLocNm,
        destLocCd: target.destLocCd,
        destLocNm: target.destLocNm,
        baseDate: cellDate,
        cellMatrix: matrix,
        selectIL: item.cd
      };
      setNoteCellData(cellData);
    } else if (["itemCdGrp", "itemNmGrp", "originLocNmGrp", "destLocNmGrp"].includes(colId)) {
      const target = e.data;
      if (target) {
        setNoteCellData({
          timeDimension: rpVersionFilter.planConfigObj.BUCKET_UNIT,
          versionDto: {...rpVersionFilter},
          itemCd: target.itemCd,
          itemNm: target.itemNm,
          originLocCd: target.originLocCd,
          originLocNm: target.originLocNm,
          destLocCd: target.destLocCd,
          destLocNm: target.destLocNm,
          selectIL: item.cd
        });
      }
    }
  };

  const handleReset = () => {
    layoutLoad.current = true;
    resetAdvancedFilter();
    setFilterValue(null);
    setItemCustomValue([]);
    setTreeLoad(true);
    setItemTypeSearch("FG");
    setItemFilterType([filterType.or.cd, filterType.equal.cd]);
    setItemFilterList({});
    setInvClassCdList([]);
    setAppliedExceptions([]);
    setMeasures(p13Measures);
    setLayoutModalData({});
    setOnlyExceptionApplied(false);
    resultTotalCount.current = 0;

    const clearParams = {
      item: null,
      categoryCds: null,
      itemType: "FG",
      destLocCd: null,
      originLocCd: null,
      onlyExceptionApplied: false,
      invClassCdList: [],
      itemFilterList: {},
      appliedExceptions: [],
      advancedFilters: null,
      itemLevelCd: null
    };

    const filter = null;
    const filterState = reduxUtil.createFilterViewState(viewName, filter);
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...filterState, advancedFilters: null }));

    setTimeout(() => {
      retrieveRpReview(clearParams, p13Measures, { reloadFalse: true });
    }, 100);
  }

  useEffect(() => {
    if (layoutModalData?.clearLayout) {
      handleReset();
    }
    if (layoutLoad.current) {
      return;
    }
    if (size(layoutModalData) > 2 && !layoutModalData?.reloadFalse) {
      layoutLoad.current = true;
      resetAdvancedFilter();
      setCurrentPage(1);
      //기존 layout과의 호환때문에 기존 item 남겨둠
      const { originLocation, destLocation, activeMeasures, itemHrchy, itemHrchyObj, itemType, exceptionsOnly, item, invClassCdList, appliedExceptions } = layoutModalData;

      const originLoc = !originLocation ? [] : Array.isArray(originLocation) ? originLocation : [originLocation];
      const destLoc = !destLocation ? [] : Array.isArray(destLocation) ? destLocation : [destLocation];

      let filter = cloneDeep(filterValue);
      filter = {...filterValue, originLocation: originLoc, destLocation: destLoc }

      const categoryies = itemHrchy ? itemHrchyObj ?
        itemHrchyObj?.filter(hrhcy => includes(itemHrchy, hrhcy.hrchyCd)) : null : null;
      setItemCustomValue(categoryies ?? []);

      const layoutHrchyCds = categoryies?.filter((category) => category?.leafFlg)?.map((category) => category?.hrchyCd);
      const filterHrchyCds = filterValue?.category?.filter((category) => category?.leafFlg)?.map((category) => category?.hrchyCd);

      if (!isEqual(layoutHrchyCds, filterHrchyCds)) {
        filter = {...filter, category: categoryies};
        setItemFilterList({});
        setItemCustomValue(categoryies ?? []);
      }
      setFilterValue({ ...filter });

      if (!isEmpty(item)) {
        layoutModalData.itemFilterList = {itemCd: [item]}
        layoutModalData.itemFilterType = [filterType.or.cd, filterType.equal.cd];
      }
      setItemFilterType(layoutModalData?.itemFilterType)
      setItemFilterList(layoutModalData?.itemFilterList)
      setItemTypeSearch(itemType);
      setInvClassCdList(invClassCdList ?? []);
      setAppliedExceptions(appliedExceptions ?? []);

      const measureCds = getSortMeasures(activeMeasures);
      setMeasures(measureCds);
      setOnlyExceptionApplied(exceptionsOnly);
      const layoutData = { ...layoutModalData, reloadFalse: true };
      setLayoutModalData(layoutData);

      const layoutParams = {
        itemFilterType: layoutModalData.itemFilterType ?? [],
        itemFilterList: layoutModalData.itemFilterList ?? {},
        categoryCds: categoryies?.map((category) => category?.hrchyCd) ?? null,
        itemType,
        destLocCd: destLoc ? destLoc.map(val=>val.locCd) : [],
        originLocCd: originLoc ? originLoc.map(val=>val.locCd) : [],
        onlyExceptionApplied: exceptionsOnly ?? false,
        invClassCdList: invClassCdList ?? [],
        appliedExceptions: appliedExceptions ?? [],
        totalCount: null,
        advancedFilters: null,
        itemLevelCd: null,
      }
      const reduxState = reduxUtil.createObjectState(viewName, { itemTypeSearch, onlyExceptionApplied, layoutModalData: layoutData,
        measures, currentPage, pageSize, initialFlg, ...layoutParams, orderByItem, invClassCdList, appliedExceptions, advancedFilters: null });
      const filterState = reduxUtil.createFilterViewState(viewName, filter);
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...filterState }));

      // After the conditions are set
      setTimeout(() => {
        retrieveRpReview(layoutParams, measureCds, layoutData);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutModalData]);

  /*
   * 세 패널이 같은 줄에 나란히 서므로 서로를 닫을 이유가 없다.
   *   예전에는 이것만 그리드 위에 떠 있어서 겹치지 않게 노트·고급 필터를 닫았다.
   */
  const handleLayoutOpen = () => {
    setLayoutOpen(!layoutOpen);
  };

  useEffect(() => {
    if (size(updateNoteData) > 0) {
      const newUpdateKey = CodeUtil.getNoteKey(viewName, updateNoteData);
      if (updateNoteData.deleteFlg) {
        noteKeySet.current.delete(newUpdateKey);
      } else {
        noteKeySet.current.add(newUpdateKey);
      }
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: columnDefs.current, viewName, initState: true });
      setUpdateNoteData({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateNoteData]);

  useEffect(() => {
    let filter = filterValue;
    if (filterData.filterList) {
      resetAdvancedFilter();
      setItemFilterList(filterData.filterList ?? {});
      setItemCustomValue([]);
      if (isEmpty(filterValue?.category)) {
        //reload
        setFilterValue({ ...filterValue, category: []});
        filter = { ...filterValue, category: []};
      } else {
        setTreeLoad(true);
      }
      setItemFilterType(filterData.filterType)
    }

    if (reduxViewState) {
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, filter, itemFilterType: filterData?.filterType, itemFilterList: filterData?.filterList}));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  useEffect(() => {
    if (itemFilterList && Object.keys(itemFilterList)?.length > 0) {
      setItemFilterColor(muicolor.INFO);
    } else {
      setItemFilterColor(muicolor.DEFAULT);
    }
    if (reduxViewState) {
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, itemFilterType, itemFilterList }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemFilterList]);

  useEffect(() => {
    if (gridLoading && !isEmpty(rpVersionFilter) && !layoutLoad.current && (itemTypeSearch || advancedFilters?.itemType) && dimensionAttrData !== null && attrConfig !== null) {
      if ((!isEmpty(measures) || !isEmpty(p13Measures)) && initLoad) {
        retrieveRpReview();
      } else if (size(layoutModalData) > 2) {
        layoutLoad.current = true;
        const measureCds = getSortMeasures(layoutModalData?.activeMeasures);
        setMeasures(measureCds);
        retrieveRpReview({}, measureCds);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, currentPage, pageSize, sortParams, dimensionAttrData, attrConfig]);

  useEffect(() => {
    if (gridLoading && !isEmpty(rpVersionFilter) && (itemTypeSearch || advancedFilters?.itemType) && !layoutLoad.current && !isEmpty(p13Measures) && orderByItem !== null) {
      if (!isEmpty(filterValue?.category)) {
        setItemFilterList({})
        setItemFilterType([filterType.or.cd, filterType.equal.cd])
      }
      if (!initLoad && !initialFlg) {
        getDefaultCustomization();
      } else {
        initPageToRetrieve();
        setInitLoad(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, measures, rpVersionFilter, itemTypeSearch, onlyExceptionApplied, p13Measures, orderByItem, invClassCdList, appliedExceptions, advancedFilters, isAFActive]);

  useEffect(() => {
    retrieveHrchyGroup();
    retrieveP13n();
    getCustomConfig();
    retrieveAttributeConfig();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpResults, viewName]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        },
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveOrderByItem = (orderByItemParam) => {
    const saveData = { pageCd: RP_REVIEW, configObj: { orderByItem: orderByItemParam } };

    p13nService.upsert(saveData)
      .then(() => {
        debug(t("MSG_SaveSuccess"));
      })
      .catch(e => {
        debug(e);
      });
  }

  const changeSortDimen = () => {
    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: columnDefs?.current });
    setOrderByItem(!orderByItem);
    saveOrderByItem(!orderByItem);
  }

  const getSortState = (params) => {
    const columnState = params.api.getColumnState();
    let sortState = sortParams ?? {};
    const sortColumn = find(columnState, column => isEqual(column.sort, "asc") || isEqual(column.sort, "desc"));
    if (isEqual(params.source, "uiColumnSorted")) {
      let sortColNm = sortColumn?.colId;
      if (includes(sortColumn?.colId, "item")) {
        sortColNm = sortColumn.colId.replace("item", "prod_");
      }
      sortState = { orderByColumn: sortColNm, sortType: sortColumn?.sort };
    }
    setSortParams(sortState);
  }

  const settingSortParams = (params) => {
    if (!dimenModalOpen) {
      getSortState(params);
    }
  }

  return (
    <>
      <FilterContainer>
        <Box>
          <Tooltip title={t("dimension")}>
            <IconButton
              size="small"
              onClick={changeSortDimen}
            >
              <Transform />
            </IconButton>
          </Tooltip>
        </Box>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={[SIMULATION, PLANNED, RELEASED]}/>
        <LocationTreeFilter viewName={viewName} title={"originLocation"} filterValue={filterValue === null || isEmpty(filterValue?.originLocation) ? null : filterValue?.originLocation?.map(location => location?.locCd)} onChangeFilter={handleFilterValueChange} />
        <LocationTreeFilter viewName={viewName} title={"destLocation"} filterValue={filterValue === null || isEmpty(filterValue?.destLocation) ? null : filterValue?.destLocation?.map(location => location?.locCd)} onChangeFilter={handleFilterValueChange} />
        <ItemHrchyTreeFilter viewName={viewName} enableSelectParent enableMultiSelect onChangeFilter={handleFilterValueChange} customValue={itemCustomValue} treeload={treeLoad}/>
        <Autocomplete
          key={"item-type-auto-complete"}
          options={itemTypes}
          getOptionLabel={option => itemType ? t(itemType[option]?.name) : option}
          value={itemTypeSearch}
          disableClearable
          onChange={(_, newValue) => handleItemTypeChange(newValue)}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ mr: 1, minWidth: 240 }}
          renderInput={(params) => (
            <TextField {...params} label={t("itemType")} />
          )}
        />
        <IconButton
          aria-describedby={"prodHrchyPop"}
          size="small"
          sx={{ mr: 1 }}
          onClick={filterActionItem}
          color={itemFilterColor}>
          <Tooltip title={t("itemFilter")}>
            <FilterAlt />
          </Tooltip>
        </IconButton>
        <AbcXyzFilterModal invClassCdList={invClassCdList} setInvClassCdList={handleInvClassCdListChange} />
        <ZeroExclusionFilter appliedExceptions={appliedExceptions} setAppliedExceptions={handleAppliedExceptionsChange} />
        <FormControlLabel
          control={<Checkbox color="primary" name={"exceptionsOnly"} checked={onlyExceptionApplied} onChange={handleCheckbox} />}
          label={t("exceptionsOnly")} key="EXCEPTIONS_ONLY" sx={{ display: "flex" }} />
        <Box sx={{ ml: "auto" }}>
          <SettingsButton label="inventoryCoverageDaysSettings" size="medium" onClick={() => setThresholdModalOpen(true)} />
          <Tooltip title={t("layout")}>
            <IconButton
              size="small"
              color={layoutOpen || size(layoutModalData) > 2 ? muicolor.INFO : muicolor.DEFAULT}
              onClick={handleLayoutOpen}>
              <ViewComfySharp />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("notes")}>
            <IconButton
              size="small"
              color="info"
              onClick={handleNoteModal}>
              <EditNote />
            </IconButton>
          </Tooltip>
          <ExcelExportButton
            title={title}
            service={rpReviewService}
            setExcelData={setGridDatas}
            columnDefs={exportColumnDefs.current}
            totalRows={resultTotalCount.current * isEmpty(measures) ? isEmpty(p13Measures) ? allMeasure : p13Measures : measures}
            params={{ ...getRequestParams(false), measures: isEmpty(measures) ? isEmpty(p13Measures) ? allMeasure : p13Measures : measures, attributes: dimensionAttrData?.attr?.reduce((acc, attrData) => { acc[attrData] = attrConfig?.item?.[attrData] ?? attrData; return acc; }, {}) }}
            isCsv
            translationColumns={["measure", "itemType"]}
            noteDefs={noteDefs}
          />
          <FilterButton label="advancedFilter" advancedFilterColor={isAFActive ? "info" : "default"} onClick={handleFilterOpen} />
        </Box>
      </FilterContainer>
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
          <SplitterContainer>
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <Box id="rp-review-grid" className="ag-theme-balham grid-area non-background-selected-row" mb={1} sx={{ display: "flex", flex: 1, minHeight: 0, height: "auto" }}>
                <Box sx={{ width: "100%", height: "calc(100% - 1px)" }}>
                  <AgGridReact
                    ref={gridRef}
                    rowData={rpResults}
                    {...defaultGridMemo}
                    rowSelection={{
                      ...defaultGridMemo.rowSelection,
                      groupSelects: "descendants",
                    }}
                    getRowStyle={getRowStyle}
                    columnDefs={defaultColumnDefs}
                    onGridReady={onGridReady}
                    onSortChanged={(params) => {
                      settingSortParams(params);
                      // 정렬은 재조회 없이 행 순서만 바뀌므로 노트 마커/그룹 첫 행 배경을 다시 계산한다
                      params.api.redrawRows();
                    }}
                    onColumnVisible={(params) => params.api.refreshCells({ force: true, columns: NOTE_MARKER_REFRESH_COLS })}
                    onRowSelected={onRowSelected}
                    onCellClicked={onCellGridClicked}
                    statusBar={statusBar}
                    groupDisplayType={'custom'}
                    groupDefaultExpanded={-1}
                    groupHideOpenParents
                    masterDetail
                    isRowMaster={detailEnabled}
                    detailCellRendererParams={detailGrid}
                    onRowDataUpdated={initCheck}
                  >
                  </AgGridReact>
                </Box>
              </Box>
              <PaginationContainer
                currentPage={currentPage} totalPages={gridPages}
                pageSize={pageSize} pageSizes={pageSizes}
                setCurrentPage={setCurrentPage} setPageSize={setPageSize}
              />
            </Box>
            <Box id="rp-review-chart" className="chart-grid-area chart-area" sx={{ height: "100%" }}>
              <PivotChartComposed
                pivotConfig={pivotConfig}
                data={chartData}
                options={{ tooltipContentStyle: { fontSize: FS.SUB }}}
                nullConvert
                activeMeasures={!isEmpty(measures) ? measures : !isEmpty(p13Measures) ? p13Measures : allMeasure}
              />
            </Box>
          </SplitterContainer>
        </Box>
        {/*
          * 우측 패널 3종은 같은 줄에 나란히 선다 — 툴바 버튼 순서(사용자 정의 → 노트 → 고급 필터)와 같게.
          * 노트는 예전에 그리드 행 **안**에 있어서 페이지네이션·차트 위까지만 닿았다 — 옆에 선
          * 사용자 정의 패널만 화면 끝까지 내려와 둘의 높이가 어긋났다. 셋 다 이 행에 둔다.
          * 감싸는 Box 는 두지 않는다: 패널이 폭 조절 손잡이를 형제로 내놓기 때문에(SidePanel resizable)
          * 감싸면 손잡이가 세로로 떨어지고 폭 상한(부모 행의 70%)도 깨진다.
          */}
        <LayoutListModal
          open={layoutOpen}
          close={setLayoutOpen}
          pageCd={viewName}
          modalResultData={setLayoutModalData}
          displayNames={displayName}
          activeMeasure={(!isEmpty(measures) ? measures : !isEmpty(p13Measures) ? p13Measures : allMeasure)
            ?.map(mea => ({ measureCd: mea, name: mea, displayName: getMeasureDisplayName(mea) }))}
          reduxViewState={reduxViewState}
        />
        {!noteOpen ? (
          <NotesModal
            version={rpVersionFilter}
            timeDimension={rpVersionFilter?.planConfigObj?.BUCKET_UNIT ?? MONTH}
            cellData={noteCellData}
            pageCd={viewName}
            setUpdateCellData={setUpdateNoteData}
            updateFlg={setNoteUpdateFlg}
            close={handleNoteModal}
          />
        ) : null}
        {openFilter && (
          <>
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={advancedFilterColumnDefs}
                advancedFilters={pendingAdvancedFilters ?? advancedFilters}
                onApply={handleAdvancedFilterApply}
                dataMap={{ levels: itemGrpLevel }}
                itemTypes={itemTypes}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          </>
        )}
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        applyMeasures={applyMeasures}
        defaultMeasure={defaultMeasure}
        availableMeasures={itemTypeSearch === CodeUtil.itemType.KIT.cd ? salesKitMeasures : []}
        displayNames={displayName}
        hasDisplayName={true}
      />
      <RPFilterModal
        open={filterModalOpen}
        close={handelFilterClose}
        displayModButtons
        modelCode={MODEL_CODE}
        modalResultData={setFilterData}
        filterTypes={{ Item: itemFilterType ?? [filterType.or.cd, filterType.equal.cd] }}
        filteredList={{ Item: itemFilterList ?? {} }}
      />
      <InvCovDaysSettings
        open={thresholdModalOpen}
        setOpen={setThresholdModalOpen}
        applyInvCovDays={setInvCovDaysConfig}
        viewName={viewName}
      />
      <DimensAttrModal
        open={dimenModalOpen}
        close={handleDimenClose}
        viewName={`${viewName}_ETC`}
        dimensionAttrData={dimensionAttrData}
        setDimensionAttrData={setDimensionAttrData}
        selectLv={{}}
        attrConfig={attrConfig}
      />
    </>
  );
};

export default withTranslation()(RpReview);
